import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, FloppyDisk, CircleNotch, Star, Tag } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const EMPTY_RP = {
  name: "",
  code: "",
  meal_plan: "none",
  price_modifier_mode: "same",
  price_modifier_value: 0,
  cancellation_policy: "flexible",
  cancellation_days: 1,
  cancellation_penalty: 0,
  is_default: false,
  active: true,
  display_order: 0,
};

const MEAL_LABELS = {
  none: "Bez obroka",
  breakfast: "Doručak (BB)",
  half_board: "Polupansion (HB)",
  full_board: "Pun pansion (FB)",
  all_inclusive: "All-inclusive (AI)",
};

const MODIFIER_LABELS = {
  same: "Ista kao bazna",
  markup: "Povećaj za %",
  markdown: "Umanji za %",
  fixed: "Fiksni iznos (bazna + X€)",
  standalone: "Neovisna cijena (potpuno zasebna)",
};

export default function RatePlansManager({ roomType, onClose }) {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | rp.id | null
  const [form, setForm] = useState(EMPTY_RP);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/room-types/${roomType.id}/rate-plans`);
      setPlans(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roomType.id]);

  const openNew = () => {
    setForm({ ...EMPTY_RP, display_order: (plans?.length || 0) });
    setEditing("new");
  };
  const openEdit = (rp) => {
    setForm({ ...EMPTY_RP, ...rp });
    setEditing(rp.id);
  };
  const closeForm = () => { setEditing(null); setForm(EMPTY_RP); };

  const set = (k) => (e) => {
    const v = e?.target?.value ?? e;
    const numFields = ["price_modifier_value", "cancellation_days", "cancellation_penalty", "display_order"];
    setForm((f) => ({ ...f, [k]: numFields.includes(k) ? Number(v) : v }));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Naziv rate plana je obavezan.");
    setSaving(true);
    const payload = form;
    try {
      if (editing === "new") {
        await api.post(`/room-types/${roomType.id}/rate-plans`, payload);
        toast.success("Rate plan kreiran i sinkroniziran s OTA-ima.");
      } else {
        await api.put(`/rate-plans/${editing}`, payload);
        toast.success("Rate plan ažuriran.");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rp) => {
    if (rp.is_default) return toast.error("Zadani rate plan se ne može obrisati. Označi drugi kao zadani prvo.");
    if (!window.confirm(`Obrisati rate plan "${rp.name}"?`)) return;
    try {
      await api.delete(`/rate-plans/${rp.id}`);
      toast.success("Rate plan obrisan.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const modifierPreview = () => {
    const base = Number(roomType.base_price || 0);
    const val = Number(form.price_modifier_value || 0);
    if (form.price_modifier_mode === "same") return base;
    if (form.price_modifier_mode === "standalone") return val;
    if (form.price_modifier_mode === "fixed") return base + val;
    if (form.price_modifier_mode === "markup") return Math.round(base * (1 + val / 100) * 100) / 100;
    if (form.price_modifier_mode === "markdown") return Math.round(base * (1 - val / 100) * 100) / 100;
    return base;
  };

  return (
    <>
      <div className="rw-sheet-backdrop fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="rw-sheet-mobile fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col rounded-t-2xl border border-border bg-card shadow-2xl sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90dvh] sm:max-w-3xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg" data-testid="rate-plans-manager">
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/25 sm:hidden" />
        <div className="flex items-center gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0 flex-1">
            <h4 className="flex items-center gap-2 truncate text-base font-semibold">
              <Tag size={16} weight="duotone" className="shrink-0 text-primary" />
              <span className="truncate">Rate planovi · {roomType.name}</span>
            </h4>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">Bazna cijena: {roomType.base_price} {roomType.currency}</p>
          </div>
          {!editing && (
            <button
              onClick={openNew}
              className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90"
              data-testid="rate-plan-new-btn"
            >
              <Plus size={14} weight="bold" /> Novi
            </button>
          )}
          <button onClick={onClose} className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6">
          {editing ? (
            <RatePlanForm
              form={form}
              set={set}
              onSave={save}
              onCancel={closeForm}
              saving={saving}
              isNew={editing === "new"}
              basePrice={roomType.base_price}
              currency={roomType.currency}
              modifierPreview={modifierPreview()}
            />
          ) : (
            <>
              {plans === null ? (
                <div className="py-10 text-center text-sm text-muted-foreground"><CircleNotch className="mx-auto mb-2 animate-spin" /> Učitavanje…</div>
              ) : plans.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-8 text-center">
                  <Tag size={24} weight="duotone" className="mx-auto text-muted-foreground" />
                  <p className="mt-2 text-sm">Još nema rate planova.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {plans.map((rp) => (
                    <RatePlanRow
                      key={rp.id}
                      rp={rp}
                      basePrice={roomType.base_price}
                      currency={roomType.currency}
                      onEdit={() => openEdit(rp)}
                      onDelete={() => remove(rp)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function RatePlanRow({ rp, basePrice, currency, onEdit, onDelete }) {
  const finalPrice = calcPrice(basePrice, rp.price_modifier_mode, rp.price_modifier_value);
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-background p-3 sm:gap-3" data-testid={`rate-plan-row-${rp.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {rp.is_default && <Star size={12} weight="fill" className="text-amber-500 shrink-0" />}
          <h5 className="truncate text-sm font-semibold">{rp.name}</h5>
          {rp.code && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-muted-foreground">{rp.code}</span>}
          {!rp.active && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">Neaktivan</span>}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1.5 truncate text-[11px] text-muted-foreground">
          <span className="truncate">{MEAL_LABELS[rp.meal_plan] || rp.meal_plan}</span>
          {rp.channex_rate_plan_id && (
            <>
              <span>·</span>
              <span className="shrink-0 text-primary">OTA sync</span>
            </>
          )}
        </div>
      </div>
      <div className="shrink-0 whitespace-nowrap text-right font-mono text-sm font-bold text-primary">{finalPrice} {currency}</div>
      <div className="flex shrink-0 items-center">
        <button onClick={onEdit} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`rate-plan-edit-${rp.id}`}><PencilSimple size={14} /></button>
        <button onClick={onDelete} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`rate-plan-delete-${rp.id}`}><Trash size={14} /></button>
      </div>
    </div>
  );
}

function RatePlanForm({ form, set, onSave, onCancel, saving, isNew, basePrice, currency, modifierPreview }) {
  return (
    <form onSubmit={onSave} className="space-y-4" data-testid="rate-plan-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Naziv rate plana *" hint="Vidi se gostu i OTA-i">
          <input required value={form.name} onChange={set("name")} className="rw-input" placeholder="npr. Bed & Breakfast" data-testid="rate-plan-name" />
        </Field>
        <Field label="Interni kod" hint="opcionalno, npr. BAR, BB, HB">
          <input value={form.code || ""} onChange={set("code")} className="rw-input" placeholder="BB" maxLength={10} />
        </Field>

        <Field label="Uključeni obroci">
          <select value={form.meal_plan} onChange={set("meal_plan")} className="rw-input" data-testid="rate-plan-meal">
            {Object.entries(MEAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>

        <Field label="Zadani rate plan?" hint="Točno jedan po tipu smještaja">
          <div className="flex items-center gap-2 pt-2">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => set("is_default")(e.target.checked)} className="h-4 w-4" data-testid="rate-plan-default" />
            <span className="text-sm">{form.is_default ? "Da — ovo je Best Available Rate" : "Ne"}</span>
          </div>
        </Field>
      </div>

      {/* Price modifier */}
      <div className="rounded-md border border-border bg-muted/20 p-4">
        <h5 className="mb-2 text-sm font-semibold">Cjenovni modifikator</h5>
        <p className="mb-3 text-xs text-muted-foreground">
          Kako se cijena ovog rate plana razlikuje od bazne cijene tipa ({basePrice} {currency}/noć).
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Način">
            <select value={form.price_modifier_mode} onChange={set("price_modifier_mode")} className="rw-input" data-testid="rate-plan-mode">
              {Object.entries(MODIFIER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          {form.price_modifier_mode !== "same" && (
            <Field label={
              form.price_modifier_mode === "standalone" ? `Cijena (${currency}/noć)` :
              form.price_modifier_mode === "fixed" ? `Vrijednost (${currency})` :
              "Postotak (%)"
            }>
              <input type="number" step="0.01" value={form.price_modifier_value} onChange={set("price_modifier_value")} className="rw-input" data-testid="rate-plan-value" />
            </Field>
          )}
        </div>
        <div className="mt-3 rounded bg-background p-2 text-xs">
          <span className="text-muted-foreground">Konačna cijena: </span>
          <span className="font-bold text-primary">{form.round_up ? Math.ceil(modifierPreview - 1e-9) : modifierPreview} {currency} / noć</span>
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!form.round_up} onChange={(e) => set("round_up")(e.target.checked)} className="h-4 w-4" data-testid="rate-plan-round-up" />
          Zaokruži cijenu na prvi veći cijeli broj (npr. 51,20 → 52)
        </label>
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" checked={!!form.active} onChange={(e) => set("active")(e.target.checked)} className="h-4 w-4" id="rp-active" />
        <label htmlFor="rp-active" className="text-sm">Aktivan (vidljiv OTA-ima)</label>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <button type="button" onClick={onCancel} className="rounded-md border border-border bg-background px-4 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
        <button type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="rate-plan-save">
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="duotone" />}
          {isNew ? "Kreiraj" : "Spremi"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function calcPrice(base, mode, value) {
  const b = Number(base || 0);
  const v = Number(value || 0);
  if (mode === "same") return b;
  if (mode === "standalone") return Math.max(v, 0);
  if (mode === "fixed") return Math.round((b + v) * 100) / 100;
  if (mode === "markup") return Math.round(b * (1 + v / 100) * 100) / 100;
  if (mode === "markdown") return Math.round(b * (1 - v / 100) * 100) / 100;
  return b;
}
